import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMoonPhase } from '../../services/ephemeris';

const MOON_GLYPH: Record<string, string> = {
  'New Moon': '●', 'Waxing Crescent': '☽', 'First Quarter': '◑',
  'Waxing Gibbous': '◕', 'Full Moon': '○', 'Waning Gibbous': '◔',
  'Last Quarter': '◐', 'Waning Crescent': '☾',
};

const now = new Date();
const moonInfo = getMoonPhase(now);
const moonGlyph = MOON_GLYPH[moonInfo.name] ?? '☾';

const weekdays = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const months   = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const todayStr = `${weekdays[now.getDay()]}, ${now.getDate()} de ${months[now.getMonth()]}`;

const LOADING_MSGS = [
  'Consultando os trânsitos de hoje...',
  'Analisando seu mapa natal...',
  'Identificando os temas do dia...',
  'Preparando sua leitura personalizada...',
];

function useRotatingMessage(messages: string[], interval = 3000, active = true): string {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setIdx(i => (i + 1) % messages.length), interval);
    return () => clearInterval(id);
  }, [active, interval, messages.length]);
  return messages[idx];
}

interface Props {
  forecastText?: string;
  loading?: boolean;
}

export function DailyBriefHero({ forecastText, loading }: Props) {
  const loadingMsg = useRotatingMessage(LOADING_MSGS, 3000, !!loading);
  return (
    <div
      style={{
        marginBottom: '1.75rem',
        minHeight: 'clamp(280px, 40vh, 420px)',
        borderTop: '1px solid var(--line-medium)',
        borderBottom: '1px solid var(--line-soft)',
        padding: 'clamp(2rem, 5vw, 3.5rem) 0',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Accent top line */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, var(--petrol), transparent)', opacity: 0.4 }} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2rem', alignItems: 'start' }}>
        {/* Left: date + reading */}
        <div>
          <p className="eyebrow" style={{ color: 'var(--petrol)', marginBottom: '0.75rem' }}>
            Hoje
          </p>
          <p style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 'clamp(1.5rem, 4vw, 2.25rem)', fontWeight: 300, color: 'rgba(240,246,252,0.92)', margin: '0 0 1.25rem', textTransform: 'capitalize', letterSpacing: '-0.01em' }}>
            {todayStr}
          </p>

          {loading ? (
            <p style={{ color: 'var(--celeste-soft)', fontSize: 'var(--text-15)', fontStyle: 'italic', fontFamily: 'var(--font-display)' }}>
              {loadingMsg}
            </p>
          ) : forecastText ? (
            <>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-18)', fontWeight: 300, color: 'rgba(200,220,235,0.88)', lineHeight: 1.75, margin: '0 0 1.5rem', maxWidth: '520px' }}>
                {(() => {
                  const plain = forecastText.replace(/^#{1,3}\s+/gm, '').replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1').trim();
                  const firstPara = plain.split('\n').filter(Boolean)[0] ?? '';
                  return firstPara.length > 300 ? firstPara.slice(0, 297) + '…' : firstPara;
                })()}
              </p>
              <Link to="/forecast" className="btn btn-secondary" style={{ fontSize: 'var(--text-13)' }}>
                Leitura completa →
              </Link>
            </>
          ) : (
            <Link to="/forecast" className="btn btn-secondary" style={{ fontSize: 'var(--text-13)' }}>
              Ver previsão do dia
            </Link>
          )}
        </div>

        {/* Right: moon phase */}
        <div style={{ textAlign: 'center', paddingTop: '0.5rem' }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: '2.75rem', color: 'rgba(240,246,252,0.85)', display: 'block', lineHeight: 1, marginBottom: '0.5rem' }}>
            {moonGlyph}
          </span>
          <p style={{ margin: 0, fontSize: 'var(--text-13)', color: 'var(--celeste-soft)', lineHeight: 1.3, maxWidth: '80px', fontFamily: 'var(--font-body)' }}>
            {moonInfo.namePt}
          </p>
          <p style={{ margin: '0.2rem 0 0', fontSize: '11px', color: 'var(--celeste-soft)', fontFamily: 'var(--font-body)' }}>
            {moonInfo.illumination}%
          </p>
        </div>
      </div>
    </div>
  );
}
